<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { computed, ref, watch } from 'vue'
import { useMutation, useQuery } from '@vue/apollo-composable'
import gql from 'graphql-tag'
import { v4 } from 'uuid'
import {
  AddFromLibraryDialogDataQuery,
  AddFromLibraryDialogDataQueryVariables,
  AddNodeFromLibraryMutation,
  AddNodeFromLibraryMutationVariables,
  GraphEntityType,
} from '@/generated/graphql'
import useAsyncDialog from '@/components/dialogs/useAsyncDialog'
import DataTable from '@/components/dataTable/DataTable.vue'
import DataTableSearch from '@/components/dataTable/DataTableSearch.vue'
import NotificationSaved from '@/components/notifications/NotificationSaved.vue'
import Period from '@/components/Period.vue'

const props = defineProps<{
  moduleId: string
}>()
const emit = defineEmits<{
  added: [nodeId: string]
}>()

const { t } = useI18n()
const { openAsyncDialog, asyncDialogResolve, asyncDialogLoading, asyncDialogIsOpen } =
  useAsyncDialog<() => Promise<void>>()

const fetchQuery = useQuery<AddFromLibraryDialogDataQuery, AddFromLibraryDialogDataQueryVariables>(
  gql`
    query AddFromLibraryDialogData($moduleId: ID!) {
      product {
        librarySteps {
          id
          name
          instructionText
          durationInSeconds
          usageCount
          stepType {
            id
            name
          }
        }
        module(id: $moduleId) {
          id
          nodes {
            id
            isLibraryReference
            step {
              id
            }
          }
        }
      }
    }
  `,
  () => ({ moduleId: props.moduleId }),
  () => ({ enabled: asyncDialogIsOpen.value }),
)
type LibraryStep = AddFromLibraryDialogDataQuery['product']['librarySteps'][number]
// A library step may be added to the same module more than once — each add creates a distinct
// placement with its own local data — so the full library is always offered. The usage-count column
// shows how often each step is already referenced.
const items = computed<LibraryStep[]>(() => fetchQuery.result.value?.product.librarySteps ?? [])

const search = ref('')
const selected = ref<LibraryStep>()
watch(asyncDialogIsOpen, (open) => {
  if (open) {
    search.value = ''
    selected.value = undefined
  }
})

const headers = [
  { key: 'selected', title: '', width: 40, sortable: false },
  { key: 'name', title: t('entity.step.field.name') },
  { key: 'stepType.name', title: t('entity.step.field.stepType') },
  { key: 'usageCount', title: t('view.organization.library.usage'), width: 90 },
]
const sort = [{ key: 'name', order: 'asc' as const }]
const onRowClick = (_: unknown, row: { item: LibraryStep }) => {
  selected.value = row.item
}

const addMutation = useMutation<AddNodeFromLibraryMutation, AddNodeFromLibraryMutationVariables>(
  gql`
    mutation AddNodeFromLibrary($command: AddTreeGraphNode!) {
      product {
        addNodeToModuleGraph(command: $command)
      }
    }
  `,
)

const notifySaved = ref(false)
const add = () => {
  if (!selected.value || !asyncDialogResolve.value) return

  const stepId = selected.value.id
  const nodeId = v4()
  asyncDialogResolve.value(async () => {
    await addMutation.mutate({
      command: {
        entityId: props.moduleId,
        node: {
          id: nodeId,
          entityType: GraphEntityType.Step,
          entityId: stepId,
          parentId: undefined,
        },
      },
    })
    notifySaved.value = true
    emit('added', nodeId)
  })
}

defineExpose({ open: openAsyncDialog })
defineOptions({ inheritAttrs: false })
</script>

<template>
  <template v-if="asyncDialogResolve">
    <v-dialog v-model="asyncDialogIsOpen" scrollable width="900" v-bind="$attrs">
      <v-card>
        <v-card-title class="d-flex align-center ga-3">
          <span>{{ t('component.addFromLibrary.title') }}</span>
          <v-spacer />
          <data-table-search v-model="search" />
        </v-card-title>

        <v-card-text>
          <data-table
            :items="items"
            :headers="headers"
            :loading="fetchQuery.loading.value"
            :sort-by="sort"
            :search="search"
            density="comfortable"
            @click:row="onRowClick"
          >
            <template #item.selected="{ item }: { item: LibraryStep }">
              <v-icon v-if="selected?.id === item.id" icon="check_circle" color="primary" />
              <v-icon v-else icon="radio_button_unchecked" color="medium-emphasis" />
            </template>
            <template #no-data>
              <p class="py-4">{{ t('component.addFromLibrary.empty') }}</p>
            </template>
          </data-table>

          <v-card v-if="selected" variant="tonal" class="mt-4">
            <v-card-title class="text-subtitle-1 d-flex align-center ga-2">
              <v-icon icon="library_add_check" size="small" />
              {{ t('component.addFromLibrary.preview') }}: {{ selected.name }}
            </v-card-title>
            <v-card-text>
              <div>
                <strong>{{ t('entity.step.field.stepType') }}:</strong> {{ selected.stepType.name }}
              </div>
              <div class="d-flex ga-1">
                <strong>{{ t('entity.step.field.durationInSeconds') }}:</strong>
                <period :seconds="selected.durationInSeconds" />
              </div>
              <div v-if="selected.instructionText" class="mt-2 text-medium-emphasis">
                {{ selected.instructionText }}
              </div>
            </v-card-text>
          </v-card>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn @click="asyncDialogResolve()">{{ t('button.cancel') }}</v-btn>
          <v-btn
            color="primary"
            variant="elevated"
            :disabled="!selected"
            :loading="asyncDialogLoading"
            @click="add"
          >
            {{ t('button.add') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </template>

  <notification-saved v-model="notifySaved" />
</template>

<style scoped lang="scss"></style>
